import { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';

interface BarcodeScannerProps {
  onScan: (barcode: string) => void;
  onClose: () => void;
  isOpen: boolean;
  continuous?: boolean;
  supportQR?: boolean;
}

export function BarcodeScanner({ onScan, onClose, isOpen, continuous = false, supportQR = false }: BarcodeScannerProps) {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastScanRef = useRef<{ text: string; time: number }>({ text: '', time: 0 });

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        if (state === 2) { // Scanning state
          await scannerRef.current.stop();
        }
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
    setIsScanning(false);
  }, []);

  const startScanner = useCallback(async () => {
    if (!containerRef.current) return;

    setError(null);
    setIsScanning(true);

    try {
      // Créer une nouvelle instance si nécessaire
      if (!scannerRef.current) {
        const formats = [
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
        ];
        if (supportQR) {
          formats.push(Html5QrcodeSupportedFormats.QR_CODE);
        }
        scannerRef.current = new Html5Qrcode('barcode-scanner-container', {
          formatsToSupport: formats,
          verbose: false,
        });
      }

      await scannerRef.current.start(
        { facingMode: 'environment' }, // Caméra arrière
        {
          fps: 10,
          qrbox: { width: 250, height: 150 },
          aspectRatio: 1.777,
        },
        (decodedText) => {
          // Debounce: ignore same barcode within 2 seconds
          const now = Date.now();
          if (continuous && decodedText === lastScanRef.current.text && now - lastScanRef.current.time < 2000) {
            return;
          }
          lastScanRef.current = { text: decodedText, time: now };

          onScan(decodedText);
          if (!continuous) {
            stopScanner();
          }
        },
        () => {
          // Erreur de scan (normal, continue de scanner)
        }
      );

      setHasPermission(true);
    } catch (err) {
      console.error('Error starting scanner:', err);
      setIsScanning(false);

      if (err instanceof Error) {
        if (err.message.includes('Permission')) {
          setHasPermission(false);
          setError('Accès à la caméra refusé. Veuillez autoriser l\'accès dans les paramètres de votre navigateur.');
        } else if (err.message.includes('NotFoundError')) {
          setError('Aucune caméra détectée sur cet appareil.');
        } else {
          setError(`Erreur: ${err.message}`);
        }
      } else {
        setError('Une erreur inattendue est survenue.');
      }
    }
  }, [onScan, stopScanner]);

  useEffect(() => {
    if (isOpen) {
      // Petit délai pour laisser le DOM se mettre à jour
      const timer = setTimeout(() => {
        startScanner();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      stopScanner();
      return undefined;
    }
  }, [isOpen, startScanner, stopScanner]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.stop().catch(() => {});
        scannerRef.current = null;
      }
    };
  }, []);

  const handleClose = () => {
    stopScanner();
    onClose();
  };

  const handleManualEntry = () => {
    const barcode = prompt('Entrez le code-barres manuellement:');
    if (barcode && barcode.trim()) {
      onScan(barcode.trim());
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/50">
        <h2 className="text-white text-lg font-semibold">Scanner un code-barres</h2>
        <Button variant="ghost" onClick={handleClose} className="text-white hover:bg-white/20">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </Button>
      </div>

      {/* Scanner Area */}
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {error ? (
          <div className="text-center">
            <div className="bg-red-500/20 border border-red-500 rounded-lg p-4 mb-4 max-w-md">
              <p className="text-red-200">{error}</p>
            </div>
            <Button onClick={startScanner} className="mr-2">
              Réessayer
            </Button>
            <Button variant="outline" onClick={handleManualEntry} className="text-white border-white hover:bg-white/20">
              Saisie manuelle
            </Button>
          </div>
        ) : (
          <>
            <div
              ref={containerRef}
              id="barcode-scanner-container"
              className="w-full max-w-md aspect-video bg-black rounded-lg overflow-hidden"
            />

            {isScanning && (
              <div className="mt-4 text-center">
                <p className="text-white/80 text-sm mb-2">
                  Placez le code-barres dans le cadre
                </p>
                <div className="flex items-center justify-center gap-2 text-white/60">
                  <Spinner size="sm" />
                  <span className="text-sm">Recherche en cours...</span>
                </div>
              </div>
            )}

            {hasPermission === null && !error && (
              <div className="mt-4 flex items-center gap-2 text-white">
                <Spinner size="sm" />
                <span>Initialisation de la caméra...</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 bg-black/50">
        <div className="flex gap-2 justify-center">
          <Button
            variant="outline"
            onClick={handleManualEntry}
            className="text-white border-white/50 hover:bg-white/20"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Saisie manuelle
          </Button>
          <Button
            variant="outline"
            onClick={handleClose}
            className="text-white border-white/50 hover:bg-white/20"
          >
            Annuler
          </Button>
        </div>
      </div>
    </div>
  );
}
